<template>
  <b-form @submit.prevent="submit">
    <h4 class="mb-2">
      {{ isEdit ? $t('usersettings.editUser') : $t('usersettings.addNewUser') }}
    </h4>

    <b-form-group :label="$t('usersettings.fullName')">
      <b-form-input
        v-model="form.name"
        required
      />
    </b-form-group>

    <b-form-group
      :label="$t('pages.email')"
      :description="ownerEmailLocked ? $t('usersettings.onlyTheAccountOwnerCanChangeThisEmail') : ''"
    >
      <b-form-input
        v-model="form.email"
        type="email"
        :disabled="ownerEmailLocked"
        required
      />
    </b-form-group>

    <b-form-group :label="$t('usersettings.department')">
      <b-form-select
        v-model="form.department"
        :options="depOptions"
        required
      />
    </b-form-group>

    <b-form-group :label="$t('usersettings.role')">
      <b-form-select
        v-model="form.role"
        :options="roleOptions"
        :disabled="userData.is_owner"
        required
      />
    </b-form-group>

    <b-form-group
      v-if="isEdit && !isPending"
      :label="$t('apps.status')"
    >
      <b-form-checkbox
        v-model="form.status"
        :value="1"
        :unchecked-value="0"
        :disabled="userData.is_owner"
      >
        {{ $t('usersettings.activeAccount') }}
      </b-form-checkbox>
    </b-form-group>

    <b-alert
      v-if="!isEdit"
      show
      variant="info"
      class="p-1"
    >
      {{ $t('usersettings.weWillEmailThisPersonALinkToSet') }}
    </b-alert>

    <div class="d-flex justify-content-end mt-2">
      <b-button
        type="submit"
        variant="primary"
      >
        {{ isEdit ? $t('usersettings.saveChanges') : $t('usersettings.createUser') }}
      </b-button>
    </div>

    <template v-if="isEdit && (isPending || canSendResetLink)">
      <hr class="my-2">
      <h5 class="mb-1">
        {{ $t('usersettings.accountAccess') }}
      </h5>

      <template v-if="isPending">
        <p class="text-muted small mb-1">
          {{ $t('usersettings.thisUserHasNotSetAPasswordYetTheir') }}
        </p>
        <b-button
          variant="outline-primary"
          size="sm"
          block
          :disabled="isSendingEmail"
          @click="sendAccessEmail('resend-activation', 'Could not resend the activation email')"
        >
          {{ $t('usersettings.resendActivationEmail') }}
        </b-button>
      </template>

      <template v-else>
        <p class="text-muted small mb-1">
          {{ $t('usersettings.theUserGetsAnEmailWithALinkTo') }}
        </p>
        <b-button
          variant="outline-secondary"
          size="sm"
          block
          :disabled="isSendingEmail"
          @click="sendAccessEmail('send-reset-link', 'Could not send the password reset email')"
        >
          {{ $t('usersettings.sendPasswordResetEmail') }}
        </b-button>
      </template>
    </template>
  </b-form>
</template>

<script>
import i18n from '@/libs/i18n'

const STATUS_ACTIVE = 1
const STATUS_PENDING = 2

export default {
  props: ['userData', 'isEdit'],
  data() {
    return {
      form: {
        name: '',
        email: '',
        department: 0,
        role: null,
        status: STATUS_ACTIVE,
        ...this.userData,
      },
      depOptions: [
        { value: 0, get text() { return i18n.t('usersettings.business') } }, { value: 1, get text() { return i18n.t('usersettings.tech') } },
        { value: 2, get text() { return i18n.t('usersettings.finance') } }, { value: 3, get text() { return i18n.t('logs.other') } },
      ],
      roleOptions: [],
      isSendingEmail: false,
    }
  },
  computed: {
    isPending() {
      return this.isEdit && this.userData.status === STATUS_PENDING
    },
    canSendResetLink() {
      return this.userData.status === STATUS_ACTIVE && this.$hasPermission('update_password')
    },
    ownerEmailLocked() {
      return Boolean(this.userData.is_owner) && !this.$store.getters['auth/isOwner']
    },
  },
  created() {
    if (this.$hasPermission('manage_users')) {
      this.fetchRoles()
    }
  },
  methods: {
    fetchRoles() {
      this.$http.get('/api/publisher-roles').then(res => {
        this.roleOptions = res.data.map(r => ({ value: r.id, text: r.display_name }))
      }).catch(errors => {
        this.roleOptions = []
        this.alertApiError(errors, i18n.t('usersettings.failedToLoadRoles'))
      })
    },
    sendAccessEmail(action, fallbackMessage) {
      this.isSendingEmail = true
      this.showLoading()
      this.$http.post(`/api/publisher-users/${this.userData.id}/${action}`)
        .then(response => {
          this.alertSuccess(response.data.message)
        })
        .catch(errors => {
          this.alertApiError(errors, fallbackMessage)
        })
        .finally(() => {
          this.isSendingEmail = false
          this.hideLoading()
        })
    },
    // Standard profile update (name, role, status)
    submit() {
      this.showLoading() // Optional: show a loader while waiting

      // 1. Explicitly pick only the fields required for the Profile Update
      const payload = {
        name: this.form.name,
        email: this.form.email,
        department: this.form.department,
        role: this.form.role,
      }

      // 2. Status is only editable for existing users who already activated their account
      if (this.isEdit && !this.isPending) {
        payload.status = this.form.status
      }

      const url = this.isEdit ? `/api/publisher-users/${this.userData.id}` : '/api/publisher-users'
      const method = this.isEdit ? 'put' : 'post'

      this.$http[method](url, payload)
        .then(response => {
          // ONLY if the backend says 'result: true'
          if (response.data.success) {
            this.alertSuccess(this.isEdit ? i18n.t('usersettings.userUpdated') : i18n.t('usersettings.userCreatedAnActivationEmailIsBeingSentTo'))
            this.$emit('saved', response.data.user) // This triggers handleUserSaved in the parent
          }
        }).catch(errors => {
          this.alertApiError(errors, this.isEdit ? i18n.t('usersettings.userUpdateFailed') : i18n.t('usersettings.userCreationFailed'))
        }).finally(() => {
          this.hideLoading()
        })
    },
  },
}
</script>
